import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import { apiUrl, handleApiError } from './utils/api';
import { formatDate } from './utils/date';

const SCOPE_OPTIONS = [
  { value: 'TRUCK', label: 'Truck' },
  { value: 'DISPATCHER', label: 'Dispatcher' },
  { value: 'PLANT', label: 'Plant' },
];

const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const number = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function mondayOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().slice(0, 10);
}

function sundayFor(startIso) {
  const d = new Date(`${startIso}T00:00:00`);
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
}

function InvoiceMetric({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Paper>
  );
}

export default function WeeklyInvoice() {
  const defaultStart = mondayOfWeek();
  const [user] = useAuthState(auth);
  const [weekStart, setWeekStart] = useState(defaultStart);
  const [weekEnd, setWeekEnd] = useState(sundayFor(defaultStart));
  const [scopeType, setScopeType] = useState('TRUCK');
  const [scopeId, setScopeId] = useState('');
  const [trucks, setTrucks] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const scopeItems = useMemo(() => {
    if (scopeType === 'TRUCK') {
      return trucks.map((truck) => ({
        id: truck.id,
        name: truck.truckNumber,
      }));
    }
    if (scopeType === 'DISPATCHER') return dispatchers;
    return plants;
  }, [dispatchers, plants, scopeType, trucks]);

  useEffect(() => {
    const loadLookups = async () => {
      setLoadingLookups(true);
      setError('');
      try {
        const [invoiceLookups, masterLookups] = await Promise.all([
          axios.get(`${apiUrl}/api/invoice/lookups`, {
            params: user?.email ? { email: user.email } : {},
          }),
          axios.get(`${apiUrl}/api/master/lookups`),
        ]);

        setTrucks(invoiceLookups.data?.truckNumberLookupList || []);
        setDispatchers(masterLookups.data?.dispatchers || []);
        setPlants(masterLookups.data?.plants || []);
      } catch (err) {
        setError(handleApiError(err, 'Failed to load weekly invoice lookups'));
      } finally {
        setLoadingLookups(false);
      }
    };

    loadLookups();
  }, [user]);

  useEffect(() => {
    setScopeId('');
    setInvoice(null);
  }, [scopeType]);

  const handleStartChange = (event) => {
    const nextStart = event.target.value;
    setWeekStart(nextStart);
    setWeekEnd(sundayFor(nextStart));
  };

  const loadInvoice = async () => {
    if (!weekStart || !weekEnd || !scopeId) {
      setError('Choose a week and a scope before generating the invoice.');
      return;
    }

    setLoadingInvoice(true);
    setError('');
    setInfo('');
    setInvoice(null);

    try {
      const response = await axios.get(`${apiUrl}/api/invoices/weekly/generate`, {
        params: {
          weekStart,
          weekEnd,
          scopeType,
          scopeId,
        },
      });

      setInvoice(response.data);
      if (!response.data?.ticketCount) {
        setInfo('No matching records found.');
      }
    } catch (err) {
      setError(handleApiError(err, 'Failed to generate weekly invoice'));
    } finally {
      setLoadingInvoice(false);
    }
  };

  const clearFilters = () => {
    const start = mondayOfWeek();
    setWeekStart(start);
    setWeekEnd(sundayFor(start));
    setScopeType('TRUCK');
    setScopeId('');
    setInvoice(null);
    setError('');
    setInfo('');
  };

  const selectedScopeLabel = scopeItems.find((item) => String(item.id) === String(scopeId))?.name || '';
  const lineItems = invoice?.lineItems || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5">Weekly Invoice</Typography>
          <Typography variant="body2" color="text.secondary">
            Generate one weekly invoice for a truck, dispatcher, or plant.
          </Typography>
        </Box>
        <ToggleButtonGroup
          color="primary"
          exclusive
          value={scopeType}
          onChange={(_, value) => value && setScopeType(value)}
          size="small"
        >
          {SCOPE_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Week Start"
              type="date"
              value={weekStart}
              onChange={handleStartChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Week End"
              type="date"
              value={weekEnd}
              onChange={(event) => setWeekEnd(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{SCOPE_OPTIONS.find((option) => option.value === scopeType)?.label}</InputLabel>
              <Select
                value={scopeId}
                label={SCOPE_OPTIONS.find((option) => option.value === scopeType)?.label}
                onChange={(event) => setScopeId(event.target.value)}
                disabled={loadingLookups}
              >
                {scopeItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name || item.code || item.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={loadInvoice}
                disabled={loadingLookups || loadingInvoice}
              >
                Generate
              </Button>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={loadingInvoice}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
      {(loadingLookups || loadingInvoice) && <CircularProgress />}

      {invoice && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <ReceiptLongIcon color="primary" />
              <Box>
                <Typography variant="h6">
                  {invoice.scopeName || selectedScopeLabel || scopeType} Invoice
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Week {invoice.weekNo}, {invoice.year} | {formatDate(invoice.weekStartDate)} to {formatDate(invoice.weekEndDate)}
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <InvoiceMetric label="Tickets" value={invoice.ticketCount || 0} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <InvoiceMetric label="Total Tons" value={number(invoice.totalTonnage)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <InvoiceMetric label="Total Amount" value={`$${money(invoice.totalAmount)}`} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <InvoiceMetric label="Scope" value={invoice.scopeType} />
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Ticket #</TableCell>
                  <TableCell>Truck</TableCell>
                  <TableCell>FS Code</TableCell>
                  <TableCell align="right">Tons</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Fuel Surcharge</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((line) => (
                  <TableRow key={line.id || line.ticketId}>
                    <TableCell>{formatDate(line.ticketDate)}</TableCell>
                    <TableCell>{line.ticketNo}</TableCell>
                    <TableCell>{line.vehicle}</TableCell>
                    <TableCell>{line.fsCode}</TableCell>
                    <TableCell align="right">{number(line.tonnage)}</TableCell>
                    <TableCell align="right">${money(line.rate)}</TableCell>
                    <TableCell align="right">${money(line.fuelSurcharge)}</TableCell>
                    <TableCell align="right">${money(line.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4}><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{number(invoice.totalTonnage)}</strong></TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right"><strong>${money(invoice.totalAmount)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
